import React, { useEffect, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";

import PermissionGuard from "@/permissions/PermissionGuard";
import { PERMISSIONS } from "@/permissions/constant";
import { DashboardService } from "@/services/dashboardService";
import useLiveResource from "@/hooks/useLiveResource";

import KPICards from "./components/KPICards";
import RecentTenants from "./components/RecentTenants";
import { Building2, Shield, User2 } from "lucide-react";

const SuperAdminDashboard = () => {

    // Setter for layout header (provided by route outlet)
    const setHeader = useOutletContext();

    // Loading and error states for dashboard data
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // KPI state storing global dashboard metrics
    const [kpis, setKpis] = useState({
        totalTenants: null,
        activeTenants: null,
        inactiveTenants: null,

        totalUsers: null,
        totalActiveUsers: null,
        totalPermission: null
    });

    // Set page header on mount and cleanup on unmount
    useEffect(() => {
        setHeader({
            title: "Dashboard",
            subTitle: "Multi Tenant platform dashboard",
        });

        return () =>
            setHeader({
                title: "",
                subTitle: "",
            });
    }, [setHeader]);

    // Fetch global super admin dashboard statistics. `silent` skips the
    // loading state — used for the background refetch a live tenant update
    // triggers, so it doesn't flash the KPI cards back to their skeletons.
    const fetchDetails = async ({ silent = false } = {}) => {
        try {
            if (!silent) setLoading(true);
            setError(null);

            // API call to fetch dashboard data
            const dashboardStats =
                await DashboardService.fetchSuperAdminDashboard();

            const stats = dashboardStats?.stats ?? {};

            // Normalize tenant-related stats (supports multiple backend formats)
            const totalTenants = stats?.totalCompanies ?? null;

            const activeTenants = stats?.activeCompanies ?? null;

            setKpis({
                totalTenants,
                activeTenants,
                // Both halves have to be numbers, or the card reads `NaN`
                // (one null) or a confident `0` (both null) for a figure the
                // server never sent.
                inactiveTenants:
                    totalTenants == null || activeTenants == null
                        ? null
                        : totalTenants - activeTenants,

                totalUsers: stats?.totalUsers ?? null,
                totalActiveUsers: stats?.activeUsers ?? null,
                totalPermission: stats?.totalPermission ?? null,
            });

        } catch (e) {
            // Show fallback error message if API fails
            setError(
                e?.message ?? "Failed to load SuperAdmin dashboard."
            );

        } finally {
            if (!silent) setLoading(false);
        }
    };

    // Fetch dashboard data on first render
    useEffect(() => {
        fetchDetails();
    }, []);

    // A tenant created, edited or (de)activated elsewhere changes the tenant
    // KPI counts above — otherwise stale until the next manual page load.
    useLiveResource({
        keys: "tenant",
        onChange: () => fetchDetails({ silent: true }),
    });

    // Prepare KPI card data for UI rendering
    const statItems = useMemo(() => {
        return [
            {
                label: "Total Tenants",
                value: kpis.totalTenants,
                icon: Building2,
                metaIdx: 0,
                route: '/super-admin/tenant',
                query: null,
            },
            {
                label: "Active Tenants",
                value: kpis.activeTenants,
                icon: Building2,
                metaIdx: 1,
                route: '/super-admin/tenant',
                query: { isActive: 'true' },
            },
            {
                label: "Inactive Tenants",
                value: kpis.inactiveTenants,
                icon: Building2,
                metaIdx: 2,
                route: '/super-admin/tenant',
                query: { isActive: 'false' },
            },
            {
                label: "Total Users (Global)",
                value: kpis.totalUsers,
                icon: User2,
                metaIdx: 0,
                route: '',
                query: null,
            },
            {
                label: "Total Active Users (Global)",
                value: kpis.totalActiveUsers,
                icon: User2,
                metaIdx: 1,
                route: '',
                query: null,
            },
            {
                label: "Total Permission",
                value: kpis.totalPermission,
                icon: Shield,
                metaIdx: 3,
                route: '/super-admin/permissions',
                query: null,
            },
        ];
    }, [kpis]);

    return (
        <PermissionGuard
            permission={PERMISSIONS.SUPER_ADMIN_MANAGE}
            isComponent
        >

            <div className="flex flex-col gap-3" style={{ zoom: 0.9 }}>

                {/* Error message display */}
                {error && (
                    <div className="px-3 py-2 rounded-lg text-[12px] bg-red-100 border border-red-300 text-red-400">
                        {error}
                    </div>
                )}

                {/* KPI summary cards */}
                <KPICards
                    statItems={statItems}
                    isLoading={loading}
                />

                {/* Recently created tenants. Fetches its own page — it does
                    not read the KPI call above. */}
                <RecentTenants />

            </div>

        </PermissionGuard>
    );
};

export default SuperAdminDashboard;